import { useCallback, useEffect, useState } from 'react';
import { local as storage } from 'utils/storage';

interface UseOneTimeTooltipOptions {
  /**
   * When to store the min version in local storage.
   * `show` means that the value will be stored when the tooltip is shown. After that, it won't be shown again, even if the user don't interact with it
   * `close` means that the value will be stored when the user closes the tooltip.
   */
  storeStateOn?: 'show' | 'close';
  /** If the tooltip is ever updated, increase this value to make the new tooltip appear */
  minVersion?: number;
}

/**
 * Returns an object to show the tooltip only once per browser
 * @param key The key of the tooltip. This is used to store in localStorage if the tooltip was shown
 * @param options The options for the tooltip
 * @returns
 */
export function useOneTimeTooltip(
  key: string,
  { storeStateOn = 'show', minVersion = 1 }: UseOneTimeTooltipOptions = {},
) {
  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(() => {
    const lastVersion = storage.getJson<number>(key) ?? 0;

    return lastVersion < minVersion;
  });

  const storeState = useCallback(() => storage.setJson(key, minVersion), [key, minVersion]);

  useEffect(() => {
    if (storeStateOn === 'show') {
      storeState();
    }
  }, [key, minVersion, storeState, storeStateOn]);

  const closeTooltip = useCallback(() => {
    setIsTooltipOpen(false);
    storeState();
  }, [storeState]);

  const closeTooltipTemporarily = useCallback(() => {
    setIsTooltipOpen(false);
  }, []);

  return { closeTooltip, closeTooltipTemporarily, isTooltipOpen, storeState };
}
